"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import {
  CircleActionButton,
  type CircleActionVariant
} from "./circle-action-button";

export interface SidebarIconRailItem {
  id: string;
  name: string;
  path?: string;
  icon: React.ReactNode;
  tone?: CircleActionVariant;
  variant?: CircleActionVariant;
  matchPaths?: string[];
  /** Subitems — presente solo en ítems DROPDOWN. */
  children?: SidebarIconRailItem[];
}

export interface SidebarIconRailV2Props {
  items: SidebarIconRailItem[];
  isCurrentPath: (path: string) => boolean;
  onNavigate: (path: string) => void;
  className?: string;
  revealItems?: boolean;
  expandAnchor?: "left" | "right";
}

function isRailItemActive(
  item: SidebarIconRailItem,
  isCurrentPath: (path: string) => boolean
): boolean {
  const paths = item.matchPaths?.length
    ? item.matchPaths
    : item.path
      ? [item.path]
      : [];
  return paths.length > 0 && paths.some((p) => isCurrentPath(p));
}

// ─── Grupo con burbujas flotantes a la derecha ────────────────────────────────

function SidebarNavGroupFlyout({
  item,
  isCurrentPath,
  onNavigate,
  expandAnchor = "left",
  revealItems = false,
  revealIndex = 0
}: {
  item: SidebarIconRailItem & { children: SidebarIconRailItem[] };
  isCurrentPath: (path: string) => boolean;
  onNavigate: (path: string) => void;
  expandAnchor?: "left" | "right";
  revealItems?: boolean;
  revealIndex?: number;
}) {
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isActive = isRailItemActive(item, isCurrentPath);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [openUp, setOpenUp] = React.useState(false);

  // Limpia el timer al desmontar
  React.useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const openGroup = React.useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  }, []);

  // Delay antes de cerrar — permite cruzar el gap entre padre e hijos
  const scheduleClose = React.useCallback(() => {
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  }, []);

  const handleChildNavigation = React.useCallback(
    (path: string) => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      setOpen(false);
      onNavigate(path);
    },
    [onNavigate]
  );

  const ITEM_H = 36; // 2.25rem en px
  const GAP = 4;     // gap-1

  /*
   * Dirección del flyout: hacia abajo por defecto. Si no hay espacio suficiente
   * bajo el trigger (cerca del borde inferior del viewport) y hay más espacio
   * arriba, se voltea hacia arriba. Se recalcula al abrir y al redimensionar.
   */
  React.useEffect(() => {
    if (!open) return;
    const decideDirection = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const count = item.children.length;
      const flyoutHeight = count * ITEM_H + (count + 1) * GAP;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUp(spaceBelow < flyoutHeight && spaceAbove > spaceBelow);
    };
    decideDirection();
    window.addEventListener("resize", decideDirection);
    return () => window.removeEventListener("resize", decideDirection);
  }, [open, item.children.length]);

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "sidebar-rail-item relative flex w-full overflow-visible",
        expandAnchor === "left" ? "justify-end" : "justify-start",
        open ? "z-[6030]" : "hover:z-[6020]",
        revealItems && "sidebar-rail-reveal-item motion-safe:opacity-0",
        revealItems &&
          "motion-safe:[animation:sidebar-rail-reveal_520ms_cubic-bezier(0.16,1,0.3,1)_forwards]"
      )}
      style={
        revealItems
          ? { animationDelay: `${80 + revealIndex * 45}ms` }
          : undefined
      }
      data-group-open={open ? "true" : undefined}
      onMouseLeave={scheduleClose}
    >
      {/* Botón padre — hover abre el grupo; pinLabel lo mantiene expandido mientras open=true */}
      <CircleActionButton
        context="sidebar"
        size="sm"
        anchor={expandAnchor}
        variant={item.variant ?? item.tone ?? "ocean"}
        active={isActive || open}
        pinLabel={open}
        expandable
        label={item.name}
        onMouseEnter={openGroup}
        aria-label={item.name}
        aria-expanded={open}
      >
        {item.icon}
      </CircleActionButton>

      {/* Hijos — aparecen debajo del padre, alineados con su ícono, ya extendidos */}
      {open && (
        <div
          className={cn(
            "absolute z-[6040] flex gap-1",
            openUp ? "flex-col-reverse" : "flex-col"
          )}
          onMouseEnter={openGroup}
          onMouseLeave={scheduleClose}
          style={{
            /*
             * left: 100% = borde derecho del rail column → los hijos flotan
             * en el área de contenido sin tapar los iconos del rail.
             * Hacia abajo: anclado a top. Hacia arriba: anclado a bottom con
             * flex-col-reverse, de modo que el primer hijo queda junto al trigger.
             */
            left: "calc(100% + 8px)",
            ...(openUp
              ? { bottom: `calc(100% + ${GAP}px)` }
              : { top: `calc(100% + ${GAP}px)` })
          }}
        >
          {item.children.map((child, i) => {
            const childActive = Boolean(
              child.path && isCurrentPath(child.path)
            );
            return (
              <div
                key={child.id}
                className={cn(
                  "sgr-child-wrap pointer-events-auto relative overflow-visible",
                  "hover:z-[6050]",
                  "motion-safe:opacity-0",
                  "motion-safe:[animation:sgr-child-reveal_440ms_cubic-bezier(0.16,1,0.3,1)_forwards]"
                )}
                style={{ animationDelay: `${i * 52}ms` }}
              >
                <CircleActionButton
                  context="sidebar"
                  size="sm"
                  anchor={expandAnchor}
                  variant={child.tone ?? item.tone ?? "ocean"}
                  active={childActive}
                  expandable
                  label={child.name}
                  onClick={() => child.path && handleChildNavigation(child.path)}
                  aria-label={child.name}
                >
                  {child.icon}
                </CircleActionButton>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Rail de iconos ───────────────────────────────────────────────────────────

export function SidebarIconRailV2({
  items,
  isCurrentPath,
  onNavigate,
  className,
  revealItems = false,
  expandAnchor = "left"
}: SidebarIconRailV2Props) {
  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Navegación principal"
      className={cn(
        "sidebar-icon-rail relative flex flex-col gap-1 overflow-visible py-1.5",
        expandAnchor === "left" ? "items-end pr-0.5" : "items-start pl-0.5",
        className
      )}
    >
      {items.map((item, index) => {
        const isDropdown = Boolean(item.children?.length);

        if (isDropdown) {
          return (
            <SidebarNavGroupFlyout
              key={item.id}
              item={
                item as SidebarIconRailItem & {
                  children: SidebarIconRailItem[];
                }
              }
              isCurrentPath={isCurrentPath}
              onNavigate={onNavigate}
              expandAnchor={expandAnchor}
              revealItems={revealItems}
              revealIndex={index}
            />
          );
        }

        return (
          <div
            key={item.id}
            className={cn(
              "sidebar-rail-item relative flex w-full overflow-visible",
              expandAnchor === "left" ? "justify-end" : "justify-start",
              "hover:z-[6020]",
              revealItems && "sidebar-rail-reveal-item motion-safe:opacity-0",
              revealItems &&
                "motion-safe:[animation:sidebar-rail-reveal_520ms_cubic-bezier(0.16,1,0.3,1)_forwards]"
            )}
            style={
              revealItems
                ? { animationDelay: `${80 + index * 45}ms` }
                : undefined
            }
          >
            <CircleActionButton
              context="sidebar"
              size="sm"
              anchor={expandAnchor}
              variant={item.variant ?? item.tone ?? "ocean"}
              active={isRailItemActive(item, isCurrentPath)}
              expandable
              label={item.name}
              onClick={() => item.path && onNavigate(item.path)}
              aria-label={item.name}
            >
              {item.icon}
            </CircleActionButton>
          </div>
        );
      })}
    </nav>
  );
}
